import { GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { Filter, LayoutGrid, List, Search } from "lucide-react";
import type { ReactNode } from "react";

export type HrQuickDatePreset = "all" | "today" | "week" | "month";

export type HrListToolbarSortOption = { value: string; label: string };

export type HrListToolbarProps = {
  searchPlaceholder: string;
  query: string;
  onQueryChange: (value: string) => void;
  filtersOpen: boolean;
  onFiltersOpenChange: (open: boolean) => void;
  appliedFiltersCount: number;
  filterSheetTitle: string;
  filterSheetDescription: string;
  onResetSheetFilters: () => void;
  filterSheetChildren: ReactNode;
  pageSize: number;
  onPageSizeChange: (n: number) => void;
  pageSizeOptions: readonly number[];
  pageSizeIdPrefix: string;
  sortBy: string;
  onSortByChange: (value: string) => void;
  sortOptions: readonly HrListToolbarSortOption[];
  quickDate: HrQuickDatePreset;
  onQuickDateChange: (v: HrQuickDatePreset) => void;
  viewMode: "grid" | "list";
  onViewModeChange: (v: "grid" | "list") => void;
  /** Hide grid/list toggle when the page only supports one layout. */
  showViewToggle?: boolean;
};

/**
 * Search + Filters sheet + per-page / sort / quick date / view controls — matches {@link Departments} toolbar layout.
 */
export function HrListToolbar({
  searchPlaceholder,
  query,
  onQueryChange,
  filtersOpen,
  onFiltersOpenChange,
  appliedFiltersCount,
  filterSheetTitle,
  filterSheetDescription,
  onResetSheetFilters,
  filterSheetChildren,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  pageSizeIdPrefix,
  sortBy,
  onSortByChange,
  sortOptions,
  quickDate,
  onQuickDateChange,
  viewMode,
  onViewModeChange,
  showViewToggle = true,
}: HrListToolbarProps) {
  const mobilePageSizeId = `${pageSizeIdPrefix}-page-size-mobile`;
  const desktopPageSizeId = `${pageSizeIdPrefix}-page-size-desktop`;

  const sortSelect = (
    <Select value={sortBy} onValueChange={onSortByChange}>
      <SelectTrigger className="glass-input h-10 w-full">
        <SelectValue placeholder="Sort by" />
      </SelectTrigger>
      <SelectContent>
        {sortOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const quickDateButtons = (
    <>
      {(
        [
          { id: "today" as const, label: "Today", mobile: "Today" },
          { id: "week" as const, label: "Week", mobile: "7D" },
          { id: "month" as const, label: "Month", mobile: "30D" },
          { id: "all" as const, label: "All", mobile: "All" },
        ] as const
      ).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onQuickDateChange(opt.id)}
          className={cn(
            "h-full px-3 text-sm transition-colors",
            quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          <span className="sm:hidden">{opt.mobile}</span>
          <span className="hidden sm:inline">{opt.label}</span>
        </button>
      ))}
    </>
  );

  const quickDateButtonsDesktop = (
    <>
      {(
        [
          { id: "today" as const, label: "Today" },
          { id: "week" as const, label: "Week" },
          { id: "month" as const, label: "Month" },
          { id: "all" as const, label: "All" },
        ] as const
      ).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onQuickDateChange(opt.id)}
          className={cn(
            "h-full px-3 text-sm transition-colors",
            quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          {opt.label}
        </button>
      ))}
    </>
  );

  const viewToggle = showViewToggle ? (
    <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
      <button
        type="button"
        onClick={() => onViewModeChange("grid")}
        className={cn(
          "h-full px-3 text-sm transition-colors",
          viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
        )}
        aria-label="Grid view"
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onViewModeChange("list")}
        className={cn(
          "h-full px-3 text-sm transition-colors",
          viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
        )}
        aria-label="List view"
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  ) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            className="glass-input pl-9 h-10"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </div>
        <Sheet open={filtersOpen} onOpenChange={onFiltersOpenChange}>
          <SheetTrigger asChild>
            <Button variant="glass" className="gap-2 shrink-0">
              <Filter className="w-4 h-4" />
              Filters
              {appliedFiltersCount > 0 ? (
                <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                  {appliedFiltersCount}
                </span>
              ) : null}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{filterSheetTitle}</SheetTitle>
              <SheetDescription>{filterSheetDescription}</SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-5">{filterSheetChildren}</div>
            <SheetFooter className="mt-6 gap-2">
              <Button type="button" variant="glass" className="w-full" onClick={onResetSheetFilters}>
                Reset
              </Button>
              <SheetClose asChild>
                <Button type="button" variant="premium" className="w-full">
                  Apply filters
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex items-center justify-between gap-2 md:hidden">
        <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
            {quickDateButtons}
          </div>
        </div>
        {viewToggle}
      </div>

      <div className="flex items-center justify-between gap-2 md:hidden">
        <GridPerPageSelect
          id={mobilePageSizeId}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          options={pageSizeOptions}
        />
        <div className="w-[170px] shrink-0">{sortSelect}</div>
      </div>

      <div className="hidden md:flex items-center justify-between gap-3">
        <GridPerPageSelect
          id={desktopPageSizeId}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          options={pageSizeOptions}
        />
        <div className="flex items-center gap-3 justify-end min-w-0">
          <div className="w-[190px] shrink-0">{sortSelect}</div>
          <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
            {quickDateButtonsDesktop}
          </div>
          {viewToggle}
        </div>
      </div>
    </div>
  );
}
